import { useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeState {
  /** User's explicit choice; `system` means "follow prefers-color-scheme". */
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
}

function prefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** Resolves a `ThemeMode` to the actual light/dark value to apply. */
export function resolveTheme(mode: ThemeMode): 'light' | 'dark' {
  return mode === 'system' ? (prefersDark() ? 'dark' : 'light') : mode;
}

/** Applies the resolved theme to the document root (`data-theme` attribute). */
export function applyTheme(mode: ThemeMode) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', resolveTheme(mode));
}

/**
 * Client/UI preference state (see `app/providers.tsx` conventions: theme is
 * client state, never server data). Persisted to localStorage; defaults to
 * `system` so a first-time visitor gets their OS preference. An inline
 * script in `index.html` applies the persisted/system theme before React
 * hydrates, avoiding a flash of the wrong theme.
 */
export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      mode: 'system',
      setMode: (mode) => {
        set({ mode });
        applyTheme(mode);
      },
    }),
    { name: 'smda-theme' },
  ),
);

/**
 * Keeps `data-theme` in sync: re-applies on mount (the persisted store may
 * resolve after the inline `index.html` script already ran) and whenever the
 * OS-level color scheme changes while `mode === 'system'`.
 */
export function useSyncTheme() {
  const mode = useThemeStore((s) => s.mode);

  useEffect(() => {
    applyTheme(mode);
    if (mode !== 'system' || typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = () => applyTheme('system');
    mediaQuery.addEventListener('change', listener);
    return () => mediaQuery.removeEventListener('change', listener);
  }, [mode]);
}
